import { PATHS } from '../../AppDomains';
import {
	ApplicationsMenuItemLinkType,
	ApplicationsMenuItemSize,
	type ApplicationsMenuItem,
} from '../../react-router/layouts/MainLayout/MainLayoutApplicationsMenu/MainLayoutApplicationsMenuItem';
import { ApplicationsMenuSelectedPage } from '../../react-router/layouts/MainLayout/MainLayoutHeader';
import { type AuthStore } from '../../stores/authStore';
import { checkIfHasPermission } from '../user-permissions/HasPermission';
import { ReactComponent as AdminToolIcon } from './icons/AdminToolIcon.svg';
import { ReactComponent as FileManagerIcon } from './icons/FileManagerIcon.svg';
import { ReactComponent as OrgDashboardIcon } from './icons/OrgDashboardIcon.svg';
import { ReactComponent as PatientPortraitIcon } from './icons/PatientPortraitIcon.svg';
import { ReactComponent as QualityOfCareIcon } from './icons/QualityOfCareIcon.svg';

export interface CreateApplicationsMenuItemsArgs {
	readonly authStore: AuthStore;
	readonly selectedPage: ApplicationsMenuSelectedPage;
	readonly isOrganizationQuality: boolean;
	readonly isAdminToolEnabled: boolean;
}

export function createApplicationsMenuItems({
	authStore,
	selectedPage,
	isOrganizationQuality,
	isAdminToolEnabled,
}: CreateApplicationsMenuItemsArgs): ReadonlyArray<ApplicationsMenuItem> {
	const hasQualityPermission = checkIfHasPermission(authStore, 'quality_tool', false);
	const hasDashboardPermission = checkIfHasPermission(authStore, 'hcc_dashboard');
	const hasFileManagerPermission = checkIfHasPermission(authStore, 'files_manager', false);
	const hasAdminToolPermission = checkIfHasPermission(authStore, 'admin_tool', false);

	const canSeeAnyApplication =
		authStore.isAdmin ||
		hasQualityPermission ||
		hasDashboardPermission ||
		hasFileManagerPermission ||
		hasAdminToolPermission;

	if (!canSeeAnyApplication) {
		return [];
	}

	const isQualityClinic = isOrganizationQuality || authStore.isAdmin;

	return [
		{
			id: 'patient-portraits',
			icon: <PatientPortraitIcon />,
			title: 'Patient Portraits',
			description: 'Concise summaries and actionable insights for your patients',
			link: PATHS.SCHEDULE_PATH,
			isSelected: selectedPage === ApplicationsMenuSelectedPage.PatientPortrait,
		},
		...(isQualityClinic && hasQualityPermission
			? [
					{
						id: 'quality-workspace',
						icon: <QualityOfCareIcon />,
						title: 'Quality Workspace',
						description: 'Assess, manage and track patient care gaps',
						link: PATHS.QUALITY_WORKSPACE_PATH,
						isSelected: selectedPage === ApplicationsMenuSelectedPage.Quality,
						linkType: ApplicationsMenuItemLinkType.External,
					},
				]
			: []),
		...(hasDashboardPermission
			? [
					{
						id: 'analytics',
						icon: <OrgDashboardIcon />,
						title: 'Analytics',
						description: 'Insights and performance tracking for your value-based care journey',
						link: PATHS.ANALYTICS_PATH,
						isSelected: selectedPage === ApplicationsMenuSelectedPage.Dashboard,
					},
				]
			: []),
		...(isAdminToolEnabled && hasAdminToolPermission
			? [
					{
						id: 'admin',
						icon: <AdminToolIcon />,
						title: 'Admin',
						description: 'Manage your users and configure permissions',
						link: PATHS.ADMIN_PATH,
						isSelected: selectedPage === ApplicationsMenuSelectedPage.Admin,
					},
				]
			: []),
		...(hasFileManagerPermission
			? [
					{
						id: 'file-manager',
						icon: <FileManagerIcon />,
						title: 'File manager',
						link: PATHS.FILES_PATH,
						isSelected: selectedPage === ApplicationsMenuSelectedPage.Files,
						size: ApplicationsMenuItemSize.Small,
						linkType: ApplicationsMenuItemLinkType.External,
					},
				]
			: []),
	];
}
